const KNOWN_UPPERCASE_TOKENS = new Set([
  "3M",
  "AIA",
  "AMD",
  "ASML",
  "AXA",
  "BHP",
  "BP",
  "BYD",
  "CME",
  "ESG",
  "ETF",
  "ETN",
  "ETP",
  "EU",
  "GE",
  "GM",
  "HDFC",
  "HSBC",
  "IBM",
  "ICBC",
  "ICICI",
  "ING",
  "JD",
  "LSE",
  "LVMH",
  "MSCI",
  "NASDAQ",
  "NYSE",
  "PDD",
  "QQQ",
  "RBC",
  "REIT",
  "SAP",
  "TD",
  "TSMC",
  "UBS",
  "UCITS",
  "US",
  "USA",
  "USD",
  "UPS",
  "ZERO",
]);

const COMMON_TITLE_CASE_TOKENS = new Set([
  "A",
  "AN",
  "AND",
  "AUTO",
  "BANK",
  "BEST",
  "BLUE",
  "BUY",
  "BY",
  "CAP",
  "CITY",
  "CO",
  "CORE",
  "DATA",
  "FOOD",
  "FOR",
  "FROM",
  "FUND",
  "GAS",
  "GOLD",
  "HOME",
  "IN",
  "LAND",
  "LIFE",
  "MAIN",
  "NEW",
  "NEXT",
  "OF",
  "OIL",
  "OLD",
  "ON",
  "OR",
  "PORT",
  "REAL",
  "STAR",
  "TECH",
  "THE",
  "TO",
  "TREE",
  "UNIT",
  "WITH",
]);

const ACRONYM_COMPANY_CONTEXT_TOKENS = new Set([
  "BANCORP",
  "BANK",
  "COMPANY",
  "CORP",
  "CORPORATION",
  "FINANCIAL",
  "GROUP",
  "HOLDING",
  "HOLDINGS",
  "INDUSTRIES",
  "NETWORKS",
  "SYSTEMS",
  "TECHNOLOGIES",
]);

const SECURITY_DESCRIPTOR_PATTERNS = [
  /\b(?:UNSPONSORED\s+)?ADR\s+REPRESENTING\b.*$/i,
  /\b(?:UNSPONSORED\s+)?ADS\s+REPRESENTING\b.*$/i,
  /\bAMERICAN\s+DEPOSITARY\s+(?:SHARES?|RECEIPTS?)\b.*$/i,
  /\bGLOBAL\s+DEPOSITARY\s+(?:SHARES?|RECEIPTS?)\b.*$/i,
  /\bORDINARY\s+SHARES?\b.*$/i,
  /\bORD\s+SHS?\b.*$/i,
  /\bCOMMON\s+STOCK\b.*$/i,
  /\bCOM\s+STK\b.*$/i,
  /\bCAPITAL\s+STOCK\b.*$/i,
  /\bSTOCK\s+(?:NEW|OLD)\b.*$/i,
  /\bSTOCK$/i,
  /\bREGISTERED\s+SHARES?\b.*$/i,
];

const TRAILING_LEGAL_SUFFIX_PATTERN =
  /(?:,?\s+|\s*\b)(?:INC\.?|INCORPORATED|CORP\.?|CORPORATION|LLC|LTD\.?|LIMITED|PLC\.?|N\.?\s*V\.?|A\s*\/\s*S|S\.?\s*A\.?\s*B\.?\s*(?:DE\s*)?C\.?\s*V\.?|S\.?\s*A\.?\s*(?:DE\s*)?C\.?\s*V\.?|S\.?\s*A\.?|AG|SE)\.?$/i;

const LEGAL_SUFFIX_BEFORE_CLASS_PATTERN =
  /(?:,?\s+|\s*\b)(?:INC\.?|INCORPORATED|CORP\.?|CORPORATION|LLC|LTD\.?|LIMITED|PLC\.?|N\.?\s*V\.?|A\s*\/\s*S|S\.?\s*A\.?\s*B\.?\s*(?:DE\s*)?C\.?\s*V\.?|S\.?\s*A\.?\s*(?:DE\s*)?C\.?\s*V\.?|S\.?\s*A\.?|AG|SE)\.?(?=\s+Class\s+[A-Z0-9]\b)/gi;

const TRAILING_NUMERIC_SERIES_PATTERN = /,\s*SERIES\s+\d+\b.*$/i;

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

const stripTrailingPunctuation = (value: string): string =>
  value.replace(/[\s,.]+$/g, "").trim();

const stripTrailingJurisdiction = (value: string): string =>
  value.replace(/\s*\([^)]*\)\s*$/g, "").trim();

const stripSecurityDescriptors = (value: string): string => {
  let result = value;

  for (const pattern of SECURITY_DESCRIPTOR_PATTERNS) {
    result = result.replace(pattern, "");
  }

  return stripTrailingPunctuation(result);
};

const getFirstComparableToken = (value: string): string | null => {
  const token = value.trim().split(/\s+/)[0];
  if (token === undefined) return null;

  const comparable = token.replace(/[^a-z0-9]/gi, "").toLowerCase();
  return comparable || null;
};

const stripFundTrustPrefix = (value: string): string => {
  const match =
    /^(?<prefix>.+?\b(?:EXCHANGE[-\s]TRADED\s+FUND\s+TRUST|ETF\s+TRUST|FUND\s+TRUST|TRUST|FUNDS|PORTFOLIOS?)\b(?:\s+(?:[IVXLCDM]+|\d+))?)(?:\s*[-:]\s*|\s+)(?<fund>.+)$/i.exec(
      value,
    );

  const prefix = match?.groups?.prefix;
  const fund = match?.groups?.fund;
  if (prefix === undefined || fund === undefined) return value;

  const prefixToken = getFirstComparableToken(prefix);
  const fundToken = getFirstComparableToken(fund);

  return prefixToken !== null && prefixToken === fundToken ? fund : value;
};

const stripLegalSuffixes = (value: string): string => {
  let result = value.replace(LEGAL_SUFFIX_BEFORE_CLASS_PATTERN, "");
  let previous: string;

  do {
    previous = result;
    result = stripTrailingPunctuation(
      result.replace(TRAILING_LEGAL_SUFFIX_PATTERN, ""),
    );
  } while (result !== previous);

  return result;
};

const stripTrailingNumericSeries = (value: string): string =>
  stripTrailingPunctuation(value.replace(TRAILING_NUMERIC_SERIES_PATTERN, ""));

const normalizeShareClass = (value: string): string =>
  value
    .replace(/\bCL\s+([A-Z0-9])\b/gi, "Class $1")
    .replace(/\bCLASS\s+([A-Z0-9])\b/gi, "Class $1");

const expandSecurityAbbreviations = (value: string): string =>
  value.replace(/\bHLDGS\b/gi, "Holdings").replace(/\bHLDG\b/gi, "Holding");

const getUppercaseComparableToken = (token: string): string =>
  token.replace(/[^a-z0-9]/gi, "").toUpperCase();

const isUppercaseToken = (token: string): boolean => {
  const letters = token.replace(/[^a-z]/gi, "");
  return letters.length > 0 && letters === letters.toUpperCase();
};

const hasAcronymSeparator = (token: string): boolean =>
  /[&./]/.test(token) && isUppercaseToken(token);

const getComparableTokenAt = (
  tokens: readonly string[],
  index: number,
): string | null => {
  const token = tokens[index];
  if (token === undefined) return null;

  const comparable = getUppercaseComparableToken(token);
  return comparable || null;
};

const shouldPreserveUppercaseToken = (
  token: string,
  tokens: readonly string[],
  index: number,
): boolean => {
  if (!isUppercaseToken(token)) return false;

  const normalized = getUppercaseComparableToken(token);
  if (!normalized) return false;
  if (KNOWN_UPPERCASE_TOKENS.has(normalized)) return true;
  if (hasAcronymSeparator(token)) return true;
  if (COMMON_TITLE_CASE_TOKENS.has(normalized)) return false;
  if (normalized.length <= 4 && !/[AEIOU]/.test(normalized)) return true;
  if (normalized.length <= 4 && tokens.length === 1) return true;

  const nextComparable = getComparableTokenAt(tokens, index + 1);
  if (
    normalized.length <= 4 &&
    index === 0 &&
    nextComparable !== null &&
    ACRONYM_COMPANY_CONTEXT_TOKENS.has(nextComparable)
  ) {
    return true;
  }

  return false;
};

const titleCaseToken = (
  token: string,
  tokens: readonly string[],
  index: number,
): string => {
  if (token.length === 0) return token;
  if (/\d/.test(token) && token.toUpperCase() === token) return token;
  if (shouldPreserveUppercaseToken(token, tokens, index)) return token;

  return token
    .split("-")
    .map((part) => {
      if (!part) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("-");
};

const shouldNormalizeCasing = (value: string): boolean => {
  const letters = value.replace(/[^A-Za-z]/g, "");
  if (!letters) return false;

  const uppercaseLetters = letters.replace(/[^A-Z]/g, "").length;
  return uppercaseLetters / letters.length > 0.75;
};

const normalizeCasing = (value: string, forceNormalize: boolean): string => {
  const cased =
    forceNormalize || shouldNormalizeCasing(value)
      ? (() => {
          const tokens = value.split(" ");
          return tokens
            .map((token, index) => titleCaseToken(token, tokens, index))
            .join(" ");
        })()
      : value;

  return cased.replace(
    /\bClass\s+([a-z0-9])\b/gi,
    (_match: string, className: string) => `Class ${className.toUpperCase()}`,
  );
};

export const sanitizeSecurityDisplayName = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  let result = trimmed;
  result = result.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
  result = normalizeWhitespace(result);
  result = stripTrailingJurisdiction(result);
  result = normalizeShareClass(result);
  result = stripSecurityDescriptors(result);
  result = stripLegalSuffixes(result);
  result = stripFundTrustPrefix(result);
  result = stripTrailingNumericSeries(result);
  const forceNormalizeCasing = shouldNormalizeCasing(result);
  result = expandSecurityAbbreviations(result);
  result = normalizeWhitespace(result);
  result = stripTrailingPunctuation(result);
  result = normalizeCasing(result, forceNormalizeCasing);

  return result || trimmed;
};
